import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IconButton } from '@mui/material';
import { Trash2, Check } from 'lucide-react';

/**
 * Two-click delete button: first click shows red checkmark, second click confirms.
 * Resets after `timeout` ms if not confirmed.
 */
export default function ConfirmDeleteButton({
  onConfirm,
  size = 'small',
  iconSize = 14,
  timeout = 2500,
  sx,
  ...rest
}) {
  const [armed, setArmed] = useState(false);
  const timer = useRef(null);

  const clear = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  }, []);

  useEffect(() => () => clear(), [clear]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (armed) {
      clear();
      setArmed(false);
      onConfirm(e);
    } else {
      setArmed(true);
      clear();
      timer.current = setTimeout(() => setArmed(false), timeout);
    }
  };

  return (
    <IconButton
      size={size}
      onClick={handleClick}
      aria-label={armed ? 'Confirm delete' : 'Delete'}
      title={armed ? 'Click again to confirm' : 'Delete'}
      sx={armed ? {
        color: '#fff',
        bgcolor: 'rgba(239, 68, 68, 0.85)',
        p: 0.35,
        borderRadius: '6px',
        '&:hover': { bgcolor: 'rgba(239, 68, 68, 1)' },
        ...sx,
      } : {
        color: 'error.main',
        p: 0.35,
        borderRadius: '6px',
        ...sx,
      }}
      {...rest}
    >
      {armed
        ? <Check size={iconSize} strokeWidth={2.5} />
        : <Trash2 size={iconSize} strokeWidth={2} />}
    </IconButton>
  );
}
